import { LanguageId } from 'src/common/constants/LanguageId';
import { ALLOWABLE_FILE_SIZE_IN_MB } from 'src/common/constants/RDFImport';
import { getAbbreviationOrFullIri } from 'src/common/utils/caches/abbreviations';
import { ellipsize } from 'src/common/utils/ellipsize';

export const components = {
  defaults: { error: 'Something went wrong.' },
  browserAuthFailedDialog: {
    closeButton: 'Close',
    homeButton: 'Stardog Cloud',
    message:
      'Could not connect to your endpoint using browser authentication. Please check your browser configuration.',
    title: 'Browser Authentication Failed',
  },
  databases: {
    otherActions: {
      loadData: {
        button: 'Load Data',
        dialogTitle: 'Load RDF',
        fileSizeExceeded: `File size exceeded. Only files under ${ALLOWABLE_FILE_SIZE_IN_MB}MB can be uploaded with Studio.`,
        graphOptionLabel: 'Load Data To',
      },
      removeData: {
        button: 'Remove Data',
        dialogTitle: 'Remove RDF',
        graphOptionLabel: 'Remove Data From',
      },
    },
    properties: {
      sensitiveProperties: {
        addButton: 'Add',
        addPropertyItem: (name: string) => `Add ${name}`,
        groupColumn: 'Groups',
        invalidCharacters:
          'The sensitive property group name is invalid. "=" is not allowed.',
        propertyColumn: 'Properties',
        propertyPlaceholder: 'Add properties',
        usedProperty: (name: string) => `Property "${name}" already exists.`,
        usedGroup: (name: string) => `Group "${name}" already exists.`,
      },
      reasoningSchemas: {
        addButton: 'Add',
        addValueItem: (name: string) => `Add ${name}`,
        keyColumn: 'Reasoning Schema',
        invalidKeyCharacters:
          'The reasoning schema name is invalid. "=" is not allowed.',
        valueColumn: 'Named Graphs',
        valuePlaceholder: 'Add named graphs',
        usedValue: (name: string) => `Named graph "${name}" already exists.`,
        usedKey: (name: string) => `Reasoning schema "${name}" already exists.`,
      },
    },
  },
  models: {
    editDisabledMessage: {
      multipleGraphs: (selectedSchemaName: string) =>
        `Editing is disabled for '${selectedSchemaName}' because it is in multiple named graphs.`,
      specialGraphs: (selectedSchemaName: string) =>
        `Editing is disabled for '${selectedSchemaName}' because it is in a special named graph.`,
    },
  },
  namedGraphSelector: {
    buttonText: (namedGraphs: string[], namespaces: string[]) =>
      namedGraphs.length === 1
        ? namedGraphs
            .map((ng) => getAbbreviationOrFullIri(ng, namespaces))
            .join(', ')
        : `${namedGraphs.length} selected`,
    noResults: 'No Results',
    placeholder: 'Search or create...',
  },
  notifications: {
    error: {
      copied: 'Copied!',
      copy: 'Copy',
      download: 'Download',
      downloaded: 'Downloaded!',
    },
  },
  statusBar: {
    connectionStatus: {
      connected: ({
        endpoint,
        name,
        username,
      }: {
        endpoint: string;
        name: string;
        username: string;
      }) => {
        let result = username ? `${username}@${endpoint}` : endpoint;
        if (name) {
          result = `${name} ${result}`;
        }
        return result;
      },
      notConnected: 'Not connected',
    },
    keyboardShortcuts: 'Keyboard Shortcuts',
    serverVersion: (isConnected: boolean, stardogVersion: string) =>
      isConnected ? `Server Version: ${stardogVersion}` : '',
  },
  storedQueries: {
    contextMenu: {
      copyDatabase: 'Copy Query Database',
      copyName: 'Copy Query Name',
      copyQuery: 'Copy Query',
      deleteQuery: 'Delete Stored Query',
      deleteQueryConfirmation: (storedQueryPathName: string) =>
        `Are you sure you want to delete ${storedQueryPathName} from your Stardog server?`,
    },
    refresh: 'Refresh List',
    search: 'Search Stored Queries ...',
    selectDatabase: 'Select Database',
    conflictingPathnameWarning: (pathName: string) =>
      `A query already exists at ${pathName}`,
    unavailableWarning: (queryName: string) =>
      `${queryName} is no longer available.`,
  },
  workspace: {
    controlBar: {
      databaseSelector: {
        search: 'Search Databases...',
        select: 'Select Database',
      },
      executeButton: {
        run: 'Run',
        stop: 'Stop',
        tooltip: {
          run: 'Run',
          stop: 'Stop',
          noQuery: 'Write a query',
          selectDb: 'Select a database',
          pending: 'Waiting for an action to complete',
          pendingDrop:
            'Selected database is being dropped. Please select another.',
        },
      },
      namedGraphSelector: {
        tooltip: {
          default: 'Set Named Graph',
          pending: 'Waiting for an action to complete',
          selectDb: 'Select a database',
          unconnected: 'Connect to a Stardog server',
        },
      },
      search: {
        placeholder: (db: string) => `Search ${db}...`,
        tooltip: {
          default: 'Search',
          noDb: 'Select a database.',
          noLucene: (db: string) =>
            `Enable "Semantic Search" for ${ellipsize(
              db
            )} in the Databases section.`,
        },
      },
      showPlanButton: {
        profiler: {
          cancelQueryQuestion: 'Are you sure you want to cancel this query?',
          run: 'Run Profiler',
          stop: 'Stop Profiler',
        },
        text: 'Show Plan',
        tooltip: {
          default: 'Show Plan',
          offline: 'The database is offline',
          noQuery: (languageId: LanguageId) =>
            `Write some ${
              languageId === LanguageId.GRAPHQL ? 'GraphQL' : 'SPARQL'
            }`,
          pending: 'Waiting for an action to complete',
          pendingDrop:
            'Selected database is being dropped. Please select another.',
          selectDb: 'Select a database',
          stop: 'Stop',
          unconnected: 'Connect to a Stardog server',
        },
      },
    },
    storedQueryPopover: {
      reasoningSwitch: 'Reasoning:',
      privateSwitch: 'Private:',
      voiceboxPromptSwitch: 'Voicebox Query Prompt',
      voiceboxQuestions: {
        exampleQuestion:
          'Your natural language question will be shown to the user. We recommend no more than 6 spotlight questions.',
        label: 'Associated question(s)',
        placeholder: 'Question that is answered by your query',
        addButton: 'Add Question',
      },
      voiceboxTags: {
        add: (tag: string) => `Create tag: ${tag}`,
        alreadyExists: (tag: string) => `${tag} already exists`,
        example: 'Voicebox Spotlight Question',
        label: 'Tags:',
        prompt: 'Voicebox Query Prompt',
      },
      triggerPromptSwitch: 'Trigger',
      triggerSettings: {
        title: 'Trigger Settings',
        limit: 'Limit:',
        schedule: 'Schedule (seconds):',
        timeout: 'Timeout (seconds):',
      },
      saveErrors: {
        emptyName: 'Query name is required',
        duplicateVoiceboxQuestion: (question: string, queryNames: string[]) =>
          `Cannot save as a Voicebox query prompt because the question "${question}" already exists in stored ${
            queryNames.length === 1
              ? `query "${queryNames[0]}"`
              : `queries ${queryNames.map((n) => `"${n}"`).join(', ')}`
          }.`,
        multipleQueries: 'Query is incomplete or invalid',
        emptyVoiceboxQuestions:
          'At least one question is required to save this query as a Voicebox Query Prompt',
        duplicateVoiceboxQuestions:
          'One or more Voicebox questions already exist in another stored query for this database',
        invalidTriggerSettings:
          'All trigger setting values are required when trigger is enabled',
      },
    },
  },
};

export const errors = {
  validation: {
    iri: {
      semicolon: 'The IRI must include a semicolon ([:]).',
      specialCharacters: 'The IRI cannot have special characters or spaces.',
      startsWith: 'The IRI must start with an alphabet character ([A-Za-z]).',
    },
    model: {
      alreadyExists: 'A model with this name already exists.',
      specialCharacters:
        'The model name cannot have special characters or spaces',
      startsWith:
        'The model name must start with an alphabet character ([A-Za-z]).',
    },
  },
  voiceboxExamplesFailure: {
    actionDescription: 'update Voicebox spotlight questions',
    message: 'Failed to update Voicebox spotlight questions',
  },
  voiceboxExamplesDeleteFailure: {
    actionDescription: 'remove Voicebox spotlight questions',
    message: 'Failed to remove Voicebox spotlight questions',
  },
};
